import { type, type Type } from 'arktype';

export const entityTypeSchema: Type<EntityType> = type("'person'|'team'|'department'|'calendar'|'location'|'skill'|'plan'").configure({
  description:
    "Entity family addressed by the grant or capability record: 'person', 'team', 'department', 'calendar', 'location', 'skill', or 'plan'.",
  examples: ['person', 'team', 'department', 'calendar', 'location', 'skill', 'plan'],
});

export type EntityType = 'person' | 'team' | 'department' | 'calendar' | 'location' | 'skill' | 'plan';

export const granteeKindSchema: Type<GranteeKind> = type("'user'|'group'").configure({
  description: 'Grant recipient kind: a user or a group (members inherit at check time).',
  examples: ['user', 'group'],
});

export type GranteeKind = 'user' | 'group';

export interface EntityGrantee {
  kind: GranteeKind;
  id: string;
}

export interface ReplaceUserEntityGrants {
  userId: string;
  permissions: string[];
}

export interface ReplaceGroupEntityGrants {
  groupId: string;
  permissions: string[];
}

export const replaceUserEntityGrantsSchema: Type<ReplaceUserEntityGrants> = type({
  userId: type('string').configure({
    description: 'ID of the grantee user. Must match the :userId path parameter.',
    examples: ['usr_abc123'],
  }),
  permissions: type('string[]').configure({
    description:
      'Full replacement set of existing rbac.permissions operation IDs. An empty array deletes all grants for the user on this entity.',
    examples: [['employees:read', 'employees:update']],
  }),
}).configure({ description: "Payload to atomically replace one user's grant set on an entity." });

export const replaceGroupEntityGrantsSchema: Type<ReplaceGroupEntityGrants> = type({
  groupId: type('string').configure({
    description: 'ID of the grantee group. Must match the :groupId path parameter.',
    examples: ['3fa85f64-5717-4562-b3fc-2c963f66afa6'],
  }),
  permissions: type('string[]').configure({
    description:
      'Full replacement set of existing rbac.permissions operation IDs. An empty array deletes all grants for the group on this entity.',
    examples: [['employees:read', 'employees:update']],
  }),
}).configure({ description: "Payload to atomically replace one group's grant set on an entity." });

export type ReplaceEntityGrants = ReplaceUserEntityGrants | ReplaceGroupEntityGrants;

/** Back-compat alias: user-grant routes keep validating user bodies. */
export const replaceEntityGrantsSchema: Type<ReplaceUserEntityGrants> = replaceUserEntityGrantsSchema;

export interface EntityGrantSet {
  kind: GranteeKind;
  userId: string | null;
  groupId: string | null;
  entityType: EntityType;
  entityId: string;
  permissions: string[];
  etag: string;
  eligibleForDirectGrant: boolean;
}

export const entityGrantSetSchema: Type<EntityGrantSet> = type({
  kind: granteeKindSchema,
  userId: type('string | null').configure({
    description: 'ID of the grantee user, or null for group grant sets.',
    examples: ['usr_abc123', null],
  }),
  groupId: type('string | null').configure({
    description: 'ID of the grantee group, or null for user grant sets.',
    examples: [null, '3fa85f64-5717-4562-b3fc-2c963f66afa6'],
  }),
  entityType: entityTypeSchema,
  entityId: type('string').configure({
    description: 'ID of the entity: canonical UUID for person/calendar/location, opaque text ID for team/skill/plan.',
    examples: ['3fa85f64-5717-4562-b3fc-2c963f66afa6', 'team-alpha'],
  }),
  permissions: type('string[]').configure({
    description: 'Granted operation permission IDs, sorted lexicographically.',
    examples: [['employees:read', 'employees:update']],
  }),
  etag: type('string').configure({
    description:
      'Opaque lowercase-hex SHA-256 fingerprint over (tenant, entityType, entityId, grantee kind, grantee id, sorted permissions). Send back as If-Match on PUT/DELETE.',
    examples: ['9f2c4e8a1b3d5f60718293a4b5c6d7e8f90123456789abcdef0123456789abcd'],
  }),
  eligibleForDirectGrant: type('boolean').configure({
    description:
      'False when the recipient is full-mode and would reject a nonempty grant save, including groups whose members all hold role-level access.',
    examples: [true, false],
  }),
}).configure({ description: 'Grant set for one grantee on one entity, with its optimistic-concurrency ETag.' });

/** User-panel row: a direct set, or a group-inherited set (read-only, edited at the group/entity level). */
export interface UserEntityAccessRow extends EntityGrantSet {
  viaGroup: { groupId: string; groupName: string } | null;
}

export interface RecipientAccessMode {
  userId: string;
  eligibleForDirectGrant: boolean;
}

export const recipientAccessModeSchema: Type<RecipientAccessMode> = type({
  userId: type('string').configure({
    description: 'ID of the user this grant-eligibility snapshot belongs to.',
    examples: ['usr_abc123'],
  }),
  eligibleForDirectGrant: type('boolean').configure({
    description: 'True when the recipient holds at least one permission at scoped scope and would accept a nonempty grant save.',
    examples: [true, false],
  }),
}).configure({ description: 'Entity-grant eligibility snapshot for one user.' });

export interface EntityCapabilities {
  entityType: EntityType;
  entityId: string;
  asOf: string;
  nextChangeAt: string | null;
  canRead: boolean;
  canWrite: boolean;
  canManageAccess: boolean;
  isOwner: boolean | null;
  readOnly: boolean;
  grantedPermissions: string[];
}

export const entityCapabilitiesSchema: Type<EntityCapabilities> = type({
  entityType: entityTypeSchema,
  entityId: type('string').configure({
    description: 'ID of the entity: canonical UUID for person/calendar/location, opaque text ID for team/skill/plan.',
    examples: ['3fa85f64-5717-4562-b3fc-2c963f66afa6', 'team-alpha'],
  }),
  asOf: type('string').configure({
    description: 'DB instant (ISO 8601 UTC) the capability decision was taken at.',
    examples: ['2026-09-14T08:15:30.000Z'],
  }),
  nextChangeAt: type('string | null').configure({
    description: 'Earliest known grant or membership transition within the 30-day window (ISO 8601 UTC), or null when none is known.',
    examples: ['2026-10-01T00:00:00.000Z', null],
  }),
  canRead: type('boolean').configure({
    description: 'Whether the caller may read this entity.',
    examples: [true, false],
  }),
  canWrite: type('boolean').configure({
    description: 'Whether the caller may mutate this entity.',
    examples: [true, false],
  }),
  canManageAccess: type('boolean').configure({
    description: 'Whether the caller may manage entity grants on this entity.',
    examples: [true, false],
  }),
  isOwner: type('boolean | null').configure({
    description: 'Calendar ownership of the caller. Null for non-calendar entities.',
    examples: [true, false, null],
  }),
  readOnly: type('boolean').configure({
    description: 'True when the caller must not mutate regardless of grants (read-only session or credential).',
    examples: [false, true],
  }),
  grantedPermissions: type('string[]').configure({
    description: "Caller's effective entity permission IDs on this entity. Empty for full-mode callers.",
    examples: [['calendars:read']],
  }),
}).configure({ description: 'Viewer-safe capability snapshot for the caller on one entity. Exposes no grant identities.' });

export interface GranteeSearchItem {
  kind: GranteeKind;
  id: string;
  label: string;
  avatarSrc: string | null;
  memberCount: number | null;
  allMembersFullMode: boolean | null;
  eligibleForDirectGrant: boolean;
}

export const granteeSearchItemSchema: Type<GranteeSearchItem> = type({
  kind: granteeKindSchema,
  id: type('string').configure({
    description: 'User or group ID of the recipient.',
    examples: ['usr_abc123'],
  }),
  label: type('string').configure({
    description: 'Human-readable display label for the recipient.',
    examples: ['Anna Müller'],
  }),
  avatarSrc: type('string | null').configure({
    description: "Profile picture of the recipient's linked person (users only), or null when unset.",
    examples: ['https://cdn.example.com/avatars/anna.jpg', null],
  }),
  memberCount: type('number | null').configure({
    description: 'Direct member count for group recipients; null for users.',
    examples: [null, 12],
  }),
  allMembersFullMode: type('boolean | null').configure({
    description:
      'For groups: true when it has members and every member is full-mode (role-level access, no scoped permission). Null for users.',
    examples: [null, true, false],
  }),
  eligibleForDirectGrant: type('boolean').configure({
    description:
      'False when the recipient is full-mode and would reject a nonempty grant save, including groups whose members all hold role-level access.',
    examples: [true, false],
  }),
}).configure({ description: 'One grant recipient from the limited recipient catalog.' });

export interface GranteeSearchResult {
  items: GranteeSearchItem[];
}

export const granteeSearchResultSchema: Type<GranteeSearchResult> = type({
  items: granteeSearchItemSchema.array().configure({
    description: 'Matching recipients: users with a linked person record the caller can already read, plus groups.',
  }),
}).configure({ description: 'Search result listing users eligible for direct entity grants.' });
